// src/pages/etudiant/LecteurFormation.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import { BookOpen, Loader2, AlertCircle, Play, CheckCircle, RotateCcw, Flag, Layers } from 'lucide-react';
import Selector from './Selector.jsx';

export default function LecteurFormation({
    formationId,
    etudiantId,
    onComplete,
    onProgress,
    onClose
}) {
    const { token } = useAuth();
    const [modules, setModules] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showMasque, setShowMasque] = useState(false);
    const [selectedModule, setSelectedModule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [progression, setProgression] = useState({});
    const [pdfToken, setPdfToken] = useState(null);
	const [videoToken, setVideoToken] = useState(null); // ⭐ AJOUT
    
    const STORAGE_KEY = `formation_${formationId}_currentModuleIndex`;

    useEffect(() => {
        if (!formationId || !token) return;

        const fetchModules = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await fetch(
                    `${APP_CONFIG.BASE_URL}/api/v1/etudiant/formations/${formationId}/modules`+(etudiantId? `?etudiantId=${etudiantId}` : ''),
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'application/json'
                        }
                    }
                );

                if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

                const data = await response.json();
               console.log("[LECTEUR FORMATION]🔍 Données brutes du backend :", data);
			   let modulesList = Array.isArray(data) ? data : (data.content || data.data || []);
			   if (modulesList.length > 0) {
			       console.log("[LECTEUR FORMATION]🔍 Clés du premier module :", Object.keys(modulesList[0]));
			       console.log("[LECTEUR FORMATION]🔍 Valeurs :", modulesList[0]);
			   }
                if (data.evaluations && data.evaluations.length > 0) {
                    const evalModules = data.evaluations.map(evaluationItem => ({
                        id: evaluationItem.id,
                        titre: evaluationItem.titre || 'Évaluation',
                        ordre: modulesList.length + 1,
                        type: 'EVALUATION',
                        evaluations: [evaluationItem],
                        content: evaluationItem.questions || []
                    }));
                    modulesList = [...modulesList, ...evalModules];
                }

                const progress = {};
                for (const mod of modulesList) {
                    const saved = localStorage.getItem(`module_progress_${mod.id}`);
                    if (saved) {
                        try {
                            progress[mod.id] = JSON.parse(saved);
                        } catch (e) { console.error(e); }
                    }
                }

                let startIndex = 0;
                const savedIndex = localStorage.getItem(STORAGE_KEY);
                if (savedIndex !== null) {
                    const idx = parseInt(savedIndex, 10);
                    if (idx >= 0 && idx < modulesList.length && !progress[modulesList[idx].id]?.completed) {
                        startIndex = idx;
                    } else {
                        const firstIncomplete = modulesList.findIndex(m => !progress[m.id]?.completed);
                        if (firstIncomplete !== -1) startIndex = firstIncomplete;
                    }
                } else {
                    const firstIncomplete = modulesList.findIndex(m => !progress[m.id]?.completed);
                    if (firstIncomplete !== -1) startIndex = firstIncomplete;
                }

                setModules(modulesList);
                setProgression(progress);
                setCurrentIndex(startIndex);

            } catch (err) {
                console.error('[LECTEUR FORMATION]Erreur chargement modules:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchModules();
    }, [formationId, token, etudiantId, STORAGE_KEY]);

    const generatePdfToken = async (moduleId) => {
        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/etudiant/modules/${moduleId}/token`+(etudiantId? `?etudiantId=${etudiantId}` : ''),
                {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            if (response.ok) {
                const data = await response.json();
                setPdfToken(data.token);
                return data.token;
            }
        } catch (err) {
            console.error('[LECTEUR FORMATION]Erreur génération token PDF:', err);
        }
        return null;
    };
	
	const generateVideoToken = async (moduleId) => { // ⭐ AJOUT
	    try {
	        const response = await fetch(
	            `${APP_CONFIG.BASE_URL}/api/v1/etudiant/modules/${moduleId}/video-token`+(etudiantId? `?etudiantId=${etudiantId}` : ''),
	            {
	                method: 'POST',
	                headers: { 'Authorization': `Bearer ${token}` }
	            }
	        );
	        if (response.ok) {
	            const data = await response.json();
	            setVideoToken(data.token);
	            return data.token;
	        }
	    } catch (err) {
	        console.error('Erreur génération token vidéo:', err);
	    }
	    return null;
	};
	
	const fetchEvaluationDetail = async (evalId) => {
	    try {
	        const response = await fetch(
	            `${APP_CONFIG.BASE_URL}/api/v1/etudiant/evaluations/${evalId}`+(etudiantId ? `?etudiantId=${etudiantId}` : ''),
	            { headers: { 'Authorization': `Bearer ${token}` } }
	        );
	        if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
	        return await response.json();
	    } catch (err) {
	        console.error(`Erreur chargement évaluation ${evalId}:`, err);
	        return null;
	    }
	};

	const fetchAllEvaluations = async () => {
	    try {
	        const response = await fetch(
	            `${APP_CONFIG.BASE_URL}/api/v1/etudiant/evaluations`+(etudiantId? `?etudiantId=${etudiantId}` : ''),
	            { headers: { 'Authorization': `Bearer ${token}` } }
	        );
	        if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
	        return await response.json();
	    } catch (err) {
	        console.error('Erreur chargement des évaluations:', err);
	        return [];
	    }
	};
	
    const fetchModuleDetail = async (moduleId) => {
        try {
            const response = await fetch(
                `${APP_CONFIG.BASE_URL}/api/v1/etudiant/modules/${moduleId}`+(etudiantId? `?etudiantId=${etudiantId}` : ''),
                { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } }
            );
            if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
            return await response.json();
        } catch (err) {
            console.error(`[LECTEUR FORMATION]Erreur chargement détail module ${moduleId}:`, err);
            return null;
        }
    };

    const getModuleType = (mod) => {
        if (mod?.type) return mod.type;
        if (mod?.hasPdf) return 'PDF';
        if (mod?.hasJsonContent) return 'JSON';
        if (mod?.hasEvaluation) return 'EVALUATION';
        if (mod?.hasVideo) return 'VIDEO';
        if (mod?.pdfStreamUrl || mod?.pdfUrl) return 'PDF';
        if (mod?.jsonContent) return 'JSON';
        if (mod?.evaluations?.length > 0) return 'EVALUATION';
        if (mod?.videoUrl || mod?.videoStreamUrl) return 'VIDEO';
        return 'UNKNOWN';
    };

    const getModuleContenu = (mod) => {
        if (!mod) return null;
        const type = getModuleType(mod);
        switch (type) {
            case 'PDF': {
				const url = mod.pdfStreamUrl || mod.pdfUrl || mod.streamUrl || mod.url || mod.pdf_stream_url || null;
				    if (!url) {
				        console.warn(`⚠️ [LecteurFormation] Module ${mod.id} marqué type=PDF mais aucune URL trouvée. Clés disponibles:`, Object.keys(mod));
				    }
					console.log( `⚠️ [LecteurFormation] URL VIDEO`,url);
				    return url;
            }
            case 'VIDEO': {
				// On cherche plusieurs noms possibles
				 const url = mod.videoStreamUrl || mod.videoUrl || mod.streamUrl || mod.url || mod.video_url || null;
				    if (!url) {
				        console.warn(`⚠️ [LecteurFormation] Module ${mod.id} marqué type=VIDEO mais aucune URL trouvée. Clés disponibles:`, Object.keys(mod));
				    }
					console.log( `⚠️ [LecteurFormation] URL VIDEO`,url);
				    return url;
            }
            case 'JSON': {
                const json = mod.jsonContent || mod.content || null;
                if (!json) {
                    console.warn(
                        `⚠️ [LecteurFormation] Module ${mod.id} marqué type=JSON mais aucun contenu trouvé. Clés disponibles:`,
                        Object.keys(mod)
                    );
                }
                return json;
            }
            // ⭐ CORRECTION : type manquant. Selector.jsx sait déjà router
            // 'JSON_RICH' vers <FormationJsonRich>, mais rien ici ne préparait
            // son contenu — le module tombait dans le `default` et ne
            // s'ouvrait jamais (voir aussi le bloc equivalent dans openMasque
            // ci-dessous).
            case 'JSON_RICH': {
                const json = mod.jsonContent || mod.content || null;
                if (!json) {
                    console.warn(
                        `⚠️ [LecteurFormation] Module ${mod.id} marqué type=JSON_RICH mais aucun contenu trouvé. Clés disponibles:`,
                        Object.keys(mod)
                    );
                }
                return json;
            }
            case 'EVALUATION': {
                const evals = (mod.evaluations && mod.evaluations.length > 0)
                    ? mod.evaluations
                    : (mod.content || []);
                if (!evals || evals.length === 0) {
                    console.warn(
                        `⚠️ [LecteurFormation] Module ${mod.id} marqué type=EVALUATION mais aucune question trouvée. Clés disponibles:`,
                        Object.keys(mod)
                    );
                }
                return evals;
            }
            default:
                console.warn(`⚠️ [LecteurFormation] Module ${mod.id} de type inconnu:`, type, mod);
                return null;
        }
    };

    const currentModule = modules[currentIndex] || modules[0];

    const openMasque = async () => {
        if (!currentModule) return;

        const type = getModuleType(currentModule);
        let moduleToOpen = { ...currentModule };

        if (type === 'PDF') {
            const pdfAuthToken = await generatePdfToken(currentModule.id);
            if (!pdfAuthToken) {
                console.error("[LECTEUR FORMATION]Impossible de générer le token PDF");
                return;
            }
            moduleToOpen.pdfStreamUrl =
                `${APP_CONFIG.BASE_URL}/api/v1/etudiant/modules/${currentModule.id}/stream?token=${pdfAuthToken}`;
		   //=======
		} else if (type === 'VIDEO') { // ⭐ AJOUT — bloc manquant
			
			 // 3) Générer un token pour cette vidéo
			const videoAuthToken = await generateVideoToken(currentModule.id);
			if (!videoAuthToken) {
				            console.error("[LECTEUR FORMATION]Impossible de générer le token vidéo");
				            return;
			}
			console.log("[LECTEUR FORMATION]videoAuthToken :",videoAuthToken);
			// 4) Construire l'URL de streaming
			moduleToOpen.videoStreamUrl =
				  `${APP_CONFIG.BASE_URL}/api/v1/etudiant/modules/${currentModule.id}/video-stream?token=${videoAuthToken}`;
			console.log("[LECTEUR FORMATION]URL vidéo construite : ",moduleToOpen.videoStreamUrl);	  
			//======================	  
       } else if ( type === 'EVALUATION') {
			console.log("DETAILEVALUATION ID MODULE : ",currentModule.id);
			console.log("DETAILEVALUATION ID EVALUATION : ",currentModule.evaluationId);
			// 1) Essayer d'obtenir les questions via fetchModuleDetail (si backend corrigé)
		    let detail = await fetchModuleDetail(currentModule.id);
			console.log("DETAILEVALUATION: ",detail);
			
			// 2) Si aucune question n'est retournée, tenter de récupérer l'évaluation via son ID
			if (!detail || !detail.questions || detail.questions.length === 0) {
			        // On cherche si le module a un evaluationId (peut être présent dans le DTO après correction)
					console.log("🔍 [LecteurFormation] currentModule :", currentModule);
					console.log("🔍 [LecteurFormation] currentModule.evaluationId :", currentModule.evaluationId);
					
					// On utilise d'abord evaluationId, puis l'ID du module lui-même en fallback
					let evalId = currentModule.evaluationId;
					console.log("🔍 [LecteurFormation] evalId choisi :", evalId);
					if (!evalId) {
						// Dernier recours : recherche par titre
					     // Recherche par titre parmi toutes les évaluations
					     const evaluations = await fetchAllEvaluations();
					     const found = evaluations.find(e => e.titre === currentModule.titre);
					     if (found) {
					          evalId = found.id;
					      }
					 }
					 if (evalId) {
					     detail = await fetchEvaluationDetail(evalId);
						 console.log("🔍 [LecteurFormation] detail après fetchEvaluationDetail :", detail);
					 }
			}
			
			// Vérification finale
			if (!detail || !detail.questions || detail.questions.length === 0) {
			    console.error(`[LECTEUR FORMATION] Impossible de charger les questions pour le module ${currentModule.id}`);
			    return;
			}
			// On place l'objet évaluation complet (avec ses questions) dans un tableau
			 moduleToOpen.evaluations = [detail];
			 
      }else if (type === 'JSON') {
			  const detail = await fetchModuleDetail(currentModule.id);
			  if (!detail) {
			       console.error(`[LECTEUR FORMATION]Impossible de charger le contenu du module ${currentModule.id}`);
			       return;
			 }
			 
			   moduleToOpen.jsonContent = detail.jsonContent ?? detail.content ?? null;
	  } else if (type === 'JSON_RICH') {
			// ⭐ AJOUT — bloc manquant : même traitement que JSON, le contenu
			// enrichi (jsonContent avec médias) n'est pas dans la liste des
			// modules, il faut aller le chercher via /modules/{id}.
			const detailRich = await fetchModuleDetail(currentModule.id);
			if (!detailRich) {
			    console.error(`[LECTEUR FORMATION]Impossible de charger le contenu JSON_RICH du module ${currentModule.id}`);
			    return;
			}
			moduleToOpen.jsonContent = detailRich.jsonContent ?? detailRich.content ?? null;
	  } else {
		console.error(`[LECTEUR FORMATION] Impossible de charger le module  ${currentModule.id}`);
					        return;	
		
		}

        setSelectedModule(moduleToOpen);
        setShowMasque(true);
    };

    const handleModuleChange = (direction) => {
        const newIndex = Math.max(0, Math.min(modules.length - 1, currentIndex + direction));
        setCurrentIndex(newIndex);
        localStorage.setItem(STORAGE_KEY, String(newIndex));
        setShowMasque(false);
    };

    const handleModuleComplete = (moduleId) => {
        const progress = progression[moduleId] || {};
        progress.completed = true;
        progress.completedAt = new Date().toISOString();
		progress.progress = 100; 
        setProgression(prev => ({ ...prev, [moduleId]: progress }));
        localStorage.setItem(`module_progress_${moduleId}`, JSON.stringify(progress));

        if (onProgress) {
            const totalCompleted = Object.values(progression).filter(p => p.completed).length + 1;
            onProgress({ moduleId, totalCompleted, totalModules: modules.length });
        }

        if (currentIndex < modules.length - 1) {
            setTimeout(() => handleModuleChange(1), 2000);
        }
    };

    const resetModule = (moduleId,index) => {
        localStorage.removeItem(`module_progress_${moduleId}`);
        setProgression(prev => {
            const newProgress = { ...prev };
            delete newProgress[moduleId];
            return newProgress;
        });
		// ⬇️ Ajout : passer à ce module
		if (index !== undefined && index !== currentIndex) {
		     setCurrentIndex(index);
		     localStorage.setItem(STORAGE_KEY, String(index));
		}
    };

    const handleFinishFormation = () => {
        if (window.confirm('Tous les modules sont terminés. Voulez-vous marquer la formation comme terminée ?')) {
            if (onComplete) {
                onComplete({ 
                    completed: true, 
                    totalModules: modules.length, 
                    progression, 
                    formationCompleted: true 
                });
            }
            onClose();
        }
    };

    // RENDU
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 animate-spin text-blue-600 mx-auto" />
                    <p className="mt-4 text-xs sm:text-sm text-gray-500 font-serif">Chargement de la formation...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center max-w-md px-4">
                    <AlertCircle className="w-12 h-12 sm:w-14 sm:h-14 text-red-500 mx-auto mb-4" />
                    <h3 className="text-base sm:text-lg font-serif font-bold text-gray-800">Erreur</h3>
                    <p className="text-xs sm:text-sm text-gray-600 font-serif">{error}</p>
                    <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }

    if (modules.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <BookOpen className="w-12 h-12 sm:w-14 sm:h-14 text-gray-300 mx-auto mb-4" />
                    <p className="text-sm sm:text-base text-gray-500 font-serif">Aucun module disponible</p>
                </div>
            </div>
        );
    }

	const completedCount = modules.filter(module => {
	    const moduleProg = progression[module.id] || {};
	    return moduleProg.completed || (moduleProg.progress && moduleProg.progress >= 100);
	}).length;
    const totalModules = modules.length;
    const allCompleted = completedCount === totalModules;

    return (
        <div className="space-y-3 sm:space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <h2 className="text-lg sm:text-2xl font-serif font-bold text-gray-800">Formation en cours</h2>
                        <p className="text-xs sm:text-sm text-gray-500 font-serif">
                            {totalModules} module(s) - {completedCount} complété(s)
                        </p>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                        <span className="text-[10px] sm:text-xs bg-green-100 text-green-700 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full font-serif">
                            {Math.round((completedCount / totalModules) * 100)}%
                        </span>
                        <button
                            onClick={onClose}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
                            title="Retour au tableau"
                        >
                            <span className="sr-only">Fermer</span>
                            ✕
                        </button>
                        {allCompleted && (
                            <button
                                onClick={handleFinishFormation}
                                className="px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg transition flex items-center gap-1"
                            >
                                <Flag className="w-4 h-4" /> Terminer
                            </button>
                        )}
                    </div>
                </div>
                <div className="mt-3 h-1.5 sm:h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${(completedCount / totalModules) * 100}%` }} />
                </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
                {modules.map((module, index) => {
                    const isCurrent = index === currentIndex;
                    //const isCompleted = progression[module.id]?.completed;
					const moduleProg = progression[module.id] || {};
					const isCompleted = moduleProg.completed || (moduleProg.progress && moduleProg.progress >= 100);
                    const progress = progression[module.id]?.progress || 0;
                    const type = getModuleType(module);

                    return (
                        <div
                            key={module.id}
                            className={`bg-white rounded-xl shadow-sm border p-3 sm:p-4 transition ${
                                isCompleted ? 'border-green-300 bg-green-50/30' :
                                isCurrent ? 'border-blue-500 bg-blue-50/30' : 'border-gray-200'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                    <span className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 ${
                                        isCompleted ? 'bg-green-600 text-white' :
                                        isCurrent ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                        {module.ordre || index + 1}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <h3 className={`text-xs sm:text-sm font-serif font-semibold truncate ${
                                                isCompleted ? 'text-gray-600' : 'text-gray-800'
                                            }`}>
                                                {module.titre}
                                            </h3>
                                            <span className={`text-[8px] sm:text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                                type === 'PDF' ? 'bg-red-100 text-red-700' :
                                                type === 'VIDEO' ? 'bg-purple-100 text-purple-700' :
                                                type === 'JSON' ? 'bg-blue-100 text-blue-700' :
                                                type === 'JSON_RICH' ? 'bg-indigo-100 text-indigo-700' :
                                                type === 'EVALUATION' ? 'bg-green-100 text-green-700' :
                                                'bg-gray-100 text-gray-600'
                                            }`}>
                                                {type === 'VIDEO' ? '🎬' : type === 'PDF' ? '📄' : type === 'JSON_RICH' ? '🧩' : type === 'JSON' ? '📝' : type === 'EVALUATION' ? '📊' : '❓'} {type}
                                            </span>
                                            {isCompleted && (
                                                <span className="text-[9px] sm:text-xs text-green-600 flex-shrink-0">
                                                    ✅ Terminé
                                                </span>
                                            )}
                                        </div>
                                        {progress > 0 && !isCompleted && (
                                            <div className="mt-1 flex items-center gap-1.5">
                                                <div className="flex-1 max-w-24 sm:max-w-32 h-1 bg-gray-200 rounded-full overflow-hidden">
                                                    <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                                                </div>
                                                <span className="text-[8px] sm:text-[10px] text-gray-400">{progress}%</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                    {!isCompleted && (
                                        <button
                                            onClick={openMasque}
                                            className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-[10px] sm:text-sm font-serif"
                                        >
                                            <Play className="w-3 h-3 sm:w-4 sm:h-4" /> Continuer
                                        </button>
                                    )}
                                    {isCompleted && (
                                        <>
                                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                                            <button
                                                onClick={() => resetModule(module.id,index)}
                                                className="p-1 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition"
                                                title="Demander la reprise de ce module"
                                            >
                                                <RotateCcw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {selectedModule && (
                <Selector
                    module={selectedModule}
                    contenu={getModuleContenu(selectedModule)}
                    formationTitre="Formation"
                    moduleOrdre={currentIndex + 1}
                    totalModules={modules.length}
                    isOpen={showMasque}
                    onClose={() => setShowMasque(false)}
                    onModuleChange={handleModuleChange}
                    onComplete={handleModuleComplete}
                    onSubmit={async (results) => {
						// Construire le payload comme dans LecteurEvaluation
						try {
						        const reponses = Object.entries(results?.answers || {}).map(([key, userAnswer]) => {
						            const questionIndex = parseInt(key.split('-')[1], 10);
						            if (Array.isArray(userAnswer)) {
						                return { questionIndex, reponsesTexte: userAnswer.map(String) };
						            }
						            if (typeof userAnswer === 'number') {
						                return { questionIndex, reponseChoisie: userAnswer };
						            }
						            return { questionIndex, reponsesTexte: [String(userAnswer)] };
						        });

						        const response = await fetch(
						            `${APP_CONFIG.BASE_URL}/api/v1/etudiant/modules/${selectedModule.id}/repondre?etudiantId=${etudiantId}`,
						            {
						                method: 'POST',
						                headers: {
						                    'Authorization': `Bearer ${token}`,
						                    'Content-Type': 'application/json'
						                },
						                body: JSON.stringify(reponses)
						            }
						        );

						        if (!response.ok) {
						            const errorData = await response.json().catch(() => ({}));
						            throw new Error(errorData.error || 'Erreur lors de la soumission');
						        }

						        const data = await response.json();
						        console.log('Évaluation soumise avec succès', data);

						        // Mettre à jour l'état local et passer au module suivant
						        handleModuleComplete(selectedModule.id);

						} catch (err) {
						        console.error('Erreur soumission évaluation module:', err);
						        alert('Erreur : ' + err.message);
						 }
                    }}
                    mode="production"
                    userRole="etudiant"
                    pdfToken={pdfToken}
					videoToken={videoToken}
					etudiantId={etudiantId}   // ⬅️ AJOUT OBLIGATOIRE
					// ⭐ CORRECTION : sans le token JWT, Selector ne peut pas le
					// relayer à <Evaluation>, qui a besoin de l'Authorization
					// header pour son appel interne /modules/{id}/evaluation/demarrer.
					token={token}
                />
            )}
        </div>
    );
}